import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ResizableBox, ResizableBoxProps } from 'react-resizable';
import TopNavbar from './TopNavbar';
import TradePanelComponent from '../trade/TradePanel';
import 'react-resizable/css/styles.css'; // Import css styles for react-resizable

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

// Main layout container
const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #121212;
  color: #e0e0e0;
`;

// Main content area - Ensure it takes full height
const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  height: 100%;
  min-height: 0;
`;

// Main chart and trading area - Adjust width dynamically based on panels
const MainArea = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
`;

// Panels container to position both panels as a group
const PanelsContainer = styled.div`
  height: 100%;
  display: flex;
  z-index: 10; // Ensure it's above other elements

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none; // Hide panels on medium screens and down
  }
`;

// Global styles to fix the resizable handles
const GlobalResizeStyles = styled.div`
  height: 100%;
  
  /* Override default handle styling */
  .react-resizable-handle {
    position: absolute;
    width: 6px;
    background-color: transparent;
    /* Hide the diagonal background image */
    background-image: none !important;
    height: 100%;
    /* Reset transforms that create the diagonal appearance */
    transform: none !important;
    z-index: 10;
  }

  /* West handle (left side) */
  .react-resizable-handle-w {
    top: 0; /* Override default positioning */
    margin-top: 0; /* Override default positioning */
    left: -3px;
    cursor: col-resize;
    /* Visual indicator for draggable area */
    &::after {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      left: 50%;
      width: 1px;
      background-color: ${({ theme }) => theme.colors.divider};
      height: 100%;
    }
    &:hover::after {
      background-color: ${({ theme }) => theme.colors.primary.main};
      width: 2px;
    }
  }
`;

// Side panel container for the ResizableBox
const SidePanelsResizable = styled(ResizableBox)`
  position: relative;
  border-left: 1px solid ${({ theme }) => theme.colors.divider};
  background: ${({ theme }) => theme.colors.background.secondary};
  height: 100%;
`;

// Side panel for Trade Panel
const TradePanel = styled.div`
  flex: 1; // Allow TradePanel to take remaining space
  overflow-y: auto;
  min-height: 0; // Prevent flexbox shrinking issues
`;

// Side panels container
const SidePanelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%; // Ensure it fills the resizable box height
  overflow: hidden; // Hide overflow within the container itself
`;

// AI Chat Panel
const AIChatPanel = styled.aside`
  display: flex;
  flex-direction: column;
  height: 100%; // Take full height
  border-left: 1px solid ${({ theme }) => theme.colors.divider};
  background-color: ${({ theme }) => theme.colors.background.secondary};
  padding: 1rem;
  overflow-y: auto;
`;

// AI Chat Resizable Container
const AIChatResizable = styled(ResizableBox)`
  position: relative;
  height: 100%;
  background: ${({ theme }) => theme.colors.background.secondary};
`;

// Add a styled button for direct testing of chat visibility
const TestButton = styled.button`
  position: fixed;
  bottom: 20px;
  left: 20px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  z-index: 1000;

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none;
  }
`;

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  // State for the currently selected trading symbol
  const [selectedSymbol, setSelectedSymbol] = useState<string>('BTC-PERP'); // Default symbol

  // State for AI Chat visibility
  const [isChatVisible, setIsChatVisible] = useState(false); // Default to hidden

  // State for sidebar width
  const [sidebarWidth, setSidebarWidth] = useState(480); // Default width increased

  // State for AI Chat panel width
  const [aiChatWidth, setAiChatWidth] = useState(300); // Default AI Chat width

  const handleSymbolChange = (symbol: string) => {
    setSelectedSymbol(symbol);
    console.log("DashboardLayout: Symbol changed to", symbol); // For debugging
  };

  // Toggle AI Chat visibility with improved logging
  const toggleChatVisibility = () => {
    console.log("Toggle chat called, current:", isChatVisible);
    setIsChatVisible(prev => !prev);
  };

  // Handle sidebar resizing
  const handleResize: ResizableBoxProps['onResize'] = (event, { size }) => {
    setSidebarWidth(size.width);
    // Optional: Save width to localStorage
    // localStorage.setItem('sidebarWidth', String(size.width));
  };

  // Handle AI Chat resizing
  const handleAIChatResize: ResizableBoxProps['onResize'] = (event, { size }) => {
    setAiChatWidth(size.width);
    // Optional: Save width to localStorage
    // localStorage.setItem('aiChatWidth', String(size.width));
  };

  // Use useEffect to log when visibility changes
  useEffect(() => {
    console.log("Chat visibility changed to:", isChatVisible);
  }, [isChatVisible]);

  // Clone children with selectedSymbol prop if the child is a React element
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { selectedSymbol } as any);
    }
    return child;
  });

  return (
    <LayoutContainer>
      <TopNavbar
        onSymbolChange={handleSymbolChange}
        onToggleChat={toggleChatVisibility}
        currentSymbol={selectedSymbol}
      />
      <ContentContainer>
        <MainArea>
          {childrenWithProps}
        </MainArea>
        
        <PanelsContainer>
          <GlobalResizeStyles>
            {/* Trade Panel with resizing */}
            <SidePanelsResizable
              width={sidebarWidth}
              axis="x"
              minConstraints={[250, Infinity]}
              maxConstraints={[600, Infinity]}
              resizeHandles={['w']}
              onResize={handleResize}
            >
              <SidePanelsContainer style={{ width: sidebarWidth }}>
                <TradePanel>
                  <TradePanelComponent selectedSymbol={selectedSymbol} />
                </TradePanel>
              </SidePanelsContainer>
            </SidePanelsResizable>
            
            {/* AI Chat Panel with resizing - Render outside GlobalResizeStyles */}
            {isChatVisible && (
              <AIChatResizable
                width={aiChatWidth}
                axis="x"
                minConstraints={[200, Infinity]}
                maxConstraints={[500, Infinity]}
                resizeHandles={['w']}
                onResize={handleAIChatResize}
              >
                <AIChatPanel style={{ width: aiChatWidth }}>
                  <h2>AI Agentic Chat</h2>
                  <div>
                    {/* Placeholder for AI Chat content */}
                    <div>Chat interface will be here</div>
                  </div>
                </AIChatPanel>
              </AIChatResizable>
            )}
          </GlobalResizeStyles>
        </PanelsContainer>
      </ContentContainer>
      
      {/* Add a test button to directly toggle chat visibility for debugging */}
      <TestButton onClick={toggleChatVisibility}>
        {isChatVisible ? 'Hide Chat' : 'Show Chat'}
      </TestButton>
    </LayoutContainer>
  );
};

export default DashboardLayout; 